import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui/useUI';
import { useAppSelector } from '@/hooks';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useMonthlyCutsLoader, useMonthlyCutAutoSelection, useCutRecalculationStatus } from '@/hooks/attendance';
import type { MonthlyCut } from '@/api/domains/attendance';
import { useHoldingCompanies } from '@/api/domains/catalog/holding/hooks/useHoldingCompanies';
import { paymentService } from '@/api/domains/payment';
import type { PaymentConceptItem } from '@/api/domains/payment';
import type { TableColumn } from '@/components/ui/tables/Table';
import type { BannerPart } from '@/components/ui/banners';
import { ActionMenu } from '@/components/ui/menus';
import type { ActionMenuItem } from '@/components/ui/menus';
import {
  useEmployeeDimensionalFilter,
  type DimensionalFilterConfig,
  type UseEmployeeDimensionalFilterResult,
} from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';
import { colors } from '@/assets/styles/colors';
import { usePaymentConceptsExport } from './usePaymentConceptsExport';


const DIMENSIONAL_CONFIG: DimensionalFilterConfig = {
  company: true,
  area: true,
  position: true,
  costCenter: true,
  contractModality: true,
  securityGroup: true,
  employeeStatus: true,
  employee: true,
};

// Claves = labels exactos de PaymentConceptStatusConstants.cs. "Enviado"/"No enviado" aún no
// tienen origen en BD; se mantienen para cuando REX+ empiece a emitirlos.
const STATUS_COLOR_MAP: Record<string, string> = {
  Generado: colors.important.main,
  Actualizado: colors.feedback.alert300,
  'Datos errados': colors.feedback.error300,
  'Error REX+': colors.feedback.error300,
  Enviado: colors.feedback.success300,
  'No enviado': colors.grays.neutral66,
  'Estado desconocido': colors.grays.neutral66,
};

function joinOrUndefined(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

/** Filtros que la pantalla acepta preaplicados por querystring (ASIS-5435). */
interface PreappliedFilters {
  monthlyCutId: number;
  companyId?: number;
  year?: number;
  month?: number;
}

// Claves de querystring que esta pantalla consume al llegar desde la columna "Conceptos" del
// listado de cortes. Solo estas se borran al consumirlas — cualquier otro parámetro de la URL
// sobrevive. Debe cubrir exactamente lo que lee `readPreappliedFilters`: si allá se agrega una
// clave, va también aquí, o quedaría en la URL y se reimpondría al recargar.
const PREAPPLIED_FILTER_KEYS = ['monthlyCutId', 'companyId', 'year', 'month'] as const;

function readPositiveInteger(searchParams: URLSearchParams, key: string): number | undefined {
  const parsed = Number(searchParams.get(key));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Lee los filtros preaplicados de la querystring. El corte es obligatorio: sin un `monthlyCutId`
 * válido no hay nada que preaplicar y la pantalla se comporta como siempre (auto-selección).
 */
function readPreappliedFilters(searchParams: URLSearchParams): PreappliedFilters | null {
  const monthlyCutId = readPositiveInteger(searchParams, 'monthlyCutId');
  if (monthlyCutId === undefined) return null;

  const month = readPositiveInteger(searchParams, 'month');

  return {
    monthlyCutId,
    companyId: readPositiveInteger(searchParams, 'companyId'),
    year: readPositiveInteger(searchParams, 'year'),
    month: month !== undefined && month <= 12 ? month : undefined,
  };
}

export interface PaymentConceptsFilters {
  monthlyCutId: number;
  // Filtros avanzados (dimensionales) — se comitean con Buscar o con cualquier auto-búsqueda
  // gatillada por un cambio de filtro principal (año/mes/descripción/empresa). Ocultar el panel
  // NO altera lo ya comiteado aquí: los valores persisten durante la sesión, sin importar si el
  // panel está expandido o colapsado (ASIS-5589).
  employeeIds: number[];
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeFilteredStatus;
}

const ALL_COMPANIES_VALUE = '';
const DEFAULT_EMPLOYEE_STATUS: EmployeeFilteredStatus = 'vigente';

export interface MonthlyCutOption {
  value: string;
  label: string;
  supportingText: string;
  /** false cuando el corte todavía está abierto; solo posible en modo empresa/holding. */
  closed: boolean;
}

/** Alias del segmento de `Banner`: se usa para "sin cortes" y para "corte en recálculo". */
export type MessagePart = BannerPart;

export interface UsePaymentConceptsListResult {
  selectedCompanyId: string;
  setSelectedCompanyId: (companyId: string) => void;
  companyOptions: Array<{ value: string; label: string }>;
  year: number | undefined;
  month: number | undefined;
  monthlyCutId: number | undefined;
  setYear: (year: number | undefined) => void;
  setMonth: (month: number | undefined) => void;
  setMonthlyCutId: (monthlyCutId: number | undefined) => void;
  yearOptions: Array<{ value: string; label: string }>;
  monthOptions: Array<{ value: string; label: string }>;
  monthlyCutOptions: MonthlyCutOption[];
  loadingMonthlyCuts: boolean;
  handleSearch: () => void;
  hasSearched: boolean;
  data: PaymentConceptItem[];
  totalRecords: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  columns: TableColumn<PaymentConceptItem>[];
  /** true cuando el scope seleccionado (empresa o TODOS) no tiene cortes disponibles. */
  noCutsAvailable: boolean;
  /** Segmentos del mensaje sin cortes (noCutsAvailable), con el énfasis ya resuelto por i18n. */
  noCutsMessageParts: MessagePart[];
  // Panel de filtros avanzados (ASIS-5317, persistencia ASIS-5589)
  dimensionalConfig: DimensionalFilterConfig;
  dimensionalFilter: UseEmployeeDimensionalFilterResult;
  /** true cuando el corte mensual seleccionado tiene un proceso de recálculo activo. */
  isCutRecalculating: boolean;
  /** true mientras el corte está en recálculo o su estado se está verificando. */
  isCutBlocked: boolean;
  /** Segmentos del mensaje de bloqueo por recálculo, con el énfasis ya resuelto por i18n. */
  recalculationMessageParts: MessagePart[];
  /** Dispara la descarga a Excel del reporte (ASIS-5320), con los filtros ya comiteados. */
  onDownload: () => void;
  /** true mientras la exportación está en curso. */
  isExporting: boolean;
  /** true cuando hay búsqueda realizada, con resultados, y el corte no está bloqueado. */
  canDownload: boolean;
}

export const usePaymentConceptsList = (): UsePaymentConceptsListResult => {
  const { t: translate } = useTranslation('payment');
  const { showApiError, openSidePanel } = useUI();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentCompanyId = useAppSelector((state) => state.auth.user?.idEmpresa ?? null);
  const { companies } = useHoldingCompanies();

  const parentCompanyId = useMemo(() => {
    const parent = companies.find((c) => !c.parentHoldingCompanyId);
    return parent ? parent.id : currentCompanyId;
  }, [companies, currentCompanyId]);

  // Filtros preaplicados por querystring (ASIS-5435): al llegar desde la columna "Conceptos" del
  // listado de cortes mensuales se siembran empresa, año, mes y corte, y la grilla se carga sola.
  // Se leen UNA sola vez, como estado inicial (no en un efecto): así ningún efecto de la pantalla
  // —en particular la auto-selección de corte— los sobrescribe en el primer render.
  const preappliedFilters = useRef(readPreappliedFilters(searchParams)).current;

  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string>(
    preappliedFilters?.companyId !== undefined
      ? String(preappliedFilters.companyId)
      : parentCompanyId ? String(parentCompanyId) : ALL_COMPANIES_VALUE,
  );
  // Con empresa preaplicada la selección se considera del usuario, para que el default del holding
  // no la sobrescriba.
  const [hasUserSelectedCompany, setHasUserSelectedCompany] = useState(
    preappliedFilters?.companyId !== undefined,
  );

  useEffect(() => {
    if (hasUserSelectedCompany) return;
    if (!parentCompanyId) return;
    setSelectedCompanyIdState(String(parentCompanyId));
  }, [parentCompanyId, hasUserSelectedCompany]);

  const companyOptions = useMemo(() => {
    const allOption = { value: ALL_COMPANIES_VALUE, label: translate('list.filters.all') };
    return [allOption, ...companies.map((c) => ({ value: String(c.id), label: c.name }))];
  }, [companies, translate]);

  const holdingScope = useMemo(() => {
    if (selectedCompanyId === ALL_COMPANIES_VALUE) return { allCompanies: true };
    const parsedCompanyId = Number(selectedCompanyId);
    return Number.isNaN(parsedCompanyId)
      ? { allCompanies: true }
      : { filterCompanyId: parsedCompanyId };
  }, [selectedCompanyId]);

  // Empresa del panel avanzado bloqueada y sincronizada con el filtro principal.
  // '' (TODOS) → [] (sin restricción); un id seleccionado → [id].
  const lockedCompanyIds = useMemo(
    () => (selectedCompanyId === ALL_COMPANIES_VALUE ? [] : [selectedCompanyId]),
    [selectedCompanyId],
  );

  const dimensionalFilter = useEmployeeDimensionalFilter(
    DIMENSIONAL_CONFIG,
    undefined,
    { lockedCompanyIds },
  );

  const {
    loadingMonthlyCuts,
    monthlyCutsWithinRange,
    yearOptions,
    monthOptions,
  } = useMonthlyCutsLoader(holdingScope, { notifyOnEmpty: false });

  const noCutsAvailable = !loadingMonthlyCuts && monthlyCutsWithinRange.length === 0;
  const noCutsMessageKeyPrefix = selectedCompanyId === ALL_COMPANIES_VALUE
    ? 'list.messages.noHoldingCuts'
    : 'list.messages.noCompanyCuts';
  const noCutsMessageParts: MessagePart[] = [
    { text: translate(`${noCutsMessageKeyPrefix}Part1`), emphasis: false },
    { text: translate(`${noCutsMessageKeyPrefix}Part1Emphasis`), emphasis: true },
    { text: translate(`${noCutsMessageKeyPrefix}Part2`), emphasis: false },
    { text: translate(`${noCutsMessageKeyPrefix}Part2Emphasis`), emphasis: true },
    { text: translate(`${noCutsMessageKeyPrefix}Part3`), emphasis: false },
  ];

  const [year, setYearState] = useState<number | undefined>(preappliedFilters?.year);
  const [month, setMonthState] = useState<number | undefined>(preappliedFilters?.month);
  const [monthlyCutId, setMonthlyCutIdState] = useState<number | undefined>(
    preappliedFilters?.monthlyCutId,
  );
  // Arranca en false incluso con filtros preaplicados: la primera consulta la dispara el efecto que
  // los aplica (más abajo), no el render inicial. Así se evita consultar con el corte equivocado
  // cuando la pantalla tiene una preferencia persistida — ver el comentario de ese efecto.
  const [hasSearched, setHasSearched] = useState(false);

  // La auto-selección de corte queda inhibida mientras rigen los filtros preaplicados: elegiría el
  // corte más reciente del catálogo y sobrescribiría el recibido (que además puede no estar en el
  // catálogo, porque solo lista cortes cerrados). En cuanto el usuario toca un filtro, la pantalla
  // vuelve a comportarse como siempre.
  const [autoSelectionEnabled, setAutoSelectionEnabled] = useState(preappliedFilters === null);

  const { isCutRecalculating, isCheckingCutStatus } = useCutRecalculationStatus(monthlyCutId);

  // `isCutRecalculating` conserva el valor del corte anterior mientras la verificación del nuevo
  // está en vuelo. Se bloquea también durante esa ventana para que no quede un hueco en el que
  // Buscar siga habilitado sobre un corte que puede estar en recálculo.
  const isCutBlocked = isCutRecalculating || isCheckingCutStatus;

  const recalculationMessageParts: MessagePart[] = [
    { text: translate('list.messages.cutRecalculatingPart1'), emphasis: false },
    { text: translate('list.messages.cutRecalculatingPart1Emphasis'), emphasis: true },
    { text: translate('list.messages.cutRecalculatingPart2'), emphasis: false },
    { text: translate('list.messages.cutRecalculatingPart2Emphasis'), emphasis: true },
    { text: translate('list.messages.cutRecalculatingPart3'), emphasis: false },
  ];

  const currentMonthOptions = useMemo(
    () => (year ? monthOptions(year) : []),
    [monthOptions, year],
  );

  const monthlyCutOptions: MonthlyCutOption[] = useMemo(() => (
    monthlyCutsWithinRange
      .filter((cut) => {
        if (year && cut.year !== year) return false;
        if (month && cut.month !== month) return false;
        return true;
      })
      .map((cut) => ({
        value: cut.monthlyCutId.toString(),
        label: cut.label,
        supportingText: cut.closed
          ? `${cut.startDate} - ${cut.endDate}`
          : `${cut.startDate} - ${cut.endDate} (${translate('list.filters.openCutSuffix')})`,
        closed: cut.closed,
      }))
  ), [monthlyCutsWithinRange, year, month, translate]);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<PaymentConceptsFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<PaymentConceptItem> | null> => {
      const response = await paymentService.listPaymentConcepts({
        monthlyCutId: params.filters.monthlyCutId,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
        employeeIds: joinOrUndefined(params.filters.employeeIds.map(String)) ?? null,
        companyIds: joinOrUndefined(params.filters.companyIds) ?? null,
        areaIds: joinOrUndefined(params.filters.areaIds) ?? null,
        positionIds: joinOrUndefined(params.filters.positionIds) ?? null,
        costCenterIds: joinOrUndefined(params.filters.costCenterIds) ?? null,
        contractModalityIds: joinOrUndefined(params.filters.contractModalityIds) ?? null,
        groupIds: joinOrUndefined(params.filters.groupIds) ?? null,
        employeeStatus: params.filters.employeeStatus,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('list.errors.loadFailed'));
        return null;
      }

      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  // Filtros dimensionales en su estado por defecto (sin commit del panel avanzado) — usados como
  // estado inicial de la grilla, antes de la primera búsqueda.
  const defaultDimensionalFilters = useMemo(() => ({
    employeeIds: [] as number[],
    companyIds: [] as string[],
    areaIds: [] as string[],
    positionIds: [] as string[],
    costCenterIds: [] as string[],
    contractModalityIds: [] as string[],
    groupIds: [] as string[],
    employeeStatus: DEFAULT_EMPLOYEE_STATUS,
  }), []);

  // Guarda el companyId que originó el cambio de empresa (no un booleano genérico): se activa en
  // setSelectedCompanyId y se lee en buildDimensionalFilters para forzar employeeIds: [] mientras
  // la auto-búsqueda que ese cambio dispara (useMonthlyCutAutoSelection) puede ejecutar una versión
  // de handleAutoSearch capturada ANTES de que el clearEmployeeSelection() de React termine de
  // propagarse a dimensionalFilter.selectedEmployeeIds — sin este override, la consulta enviaría
  // employeeIds de la empresa anterior, que prevalece sobre los dimensionales en el backend
  // (ASIS-5589). Se invalida (null) apenas el usuario vuelve a seleccionar colaboradores para la
  // empresa nueva, así que una selección hecha durante la resolución async del corte no se pierde
  // (hallazgo de code review de Fran-Alarcon en la PR #346).
  const companyJustChangedRef = useRef<string | null>(null);

  useEffect(() => {
    if (dimensionalFilter.selectedEmployeeIds.length === 0) return;
    companyJustChangedRef.current = null;
  }, [dimensionalFilter.selectedEmployeeIds]);

  // Snapshot vigente del panel avanzado (dimensionales + colaboradores seleccionados), sin
  // importar si está expandido. Lo consumen tanto Buscar (panel abierto) como la auto-búsqueda
  // por cambio de filtro principal, esté el panel expandido o colapsado (ASIS-5589). Builder puro:
  // no muta refs, solo lee el estado vigente del panel y el override de cambio de empresa.
  const buildDimensionalFilters = useCallback(() => {
    const employeeIds = companyJustChangedRef.current === selectedCompanyId
      ? []
      : dimensionalFilter.selectedEmployeeIds;
    return {
      employeeIds,
      companyIds: dimensionalFilter.dimensionalFilters.companyIds,
      areaIds: dimensionalFilter.dimensionalFilters.areaIds,
      positionIds: dimensionalFilter.dimensionalFilters.positionIds,
      costCenterIds: dimensionalFilter.dimensionalFilters.costCenterIds,
      contractModalityIds: dimensionalFilter.dimensionalFilters.contractModalityIds,
      groupIds: dimensionalFilter.dimensionalFilters.groupIds,
      employeeStatus: dimensionalFilter.dimensionalFilters.status,
    };
  }, [
    selectedCompanyId,
    dimensionalFilter.selectedEmployeeIds,
    dimensionalFilter.dimensionalFilters,
  ]);

  const {
    rows,
    total: totalRecords,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    setFilters,
    filters: committedFilters,
    debouncedSearchText,
  } = usePaginatedList<PaymentConceptItem, PaymentConceptsFilters>({
    fetchFn,
    initialFilters: {
      monthlyCutId: preappliedFilters?.monthlyCutId ?? 0,
      ...defaultDimensionalFilters,
    },
    initialSort: { key: 'generatedAt', direction: 'DESC' },
    enabled: hasSearched && !isCutBlocked,
    // orderByMap intentionally omitted: PaymentConceptItem's field names (and therefore each
    // column's `key`) are aligned with the backend's orderBy whitelist
    // (PaymentConceptOrderByFields), so usePaginatedList's `columnKey` fallback already sends
    // the correct backend field with no translation needed.
    // screenCode intentionally omitted: this screen's panel (year/month/monthlyCutId,
    // dimensionalFilter) lives outside usePaginatedList's `filters` and isn't hydrated by it —
    // persisting `filters` here would restore a monthlyCutId the visible panel doesn't reflect.
    // Re-adopt once the panel has a hydration bridge (see PR #263 review).
  });

  // Exportación a Excel (ASIS-5320): usa los filtros ya comiteados por la grilla (los mismos que
  // trajeron los resultados en pantalla), no el estado en edición del panel avanzado.
  const { isExporting, onExport } = usePaymentConceptsExport({
    monthlyCutId: committedFilters.monthlyCutId,
    employeeIds: committedFilters.employeeIds,
    companyIds: committedFilters.companyIds,
    areaIds: committedFilters.areaIds,
    positionIds: committedFilters.positionIds,
    costCenterIds: committedFilters.costCenterIds,
    contractModalityIds: committedFilters.contractModalityIds,
    groupIds: committedFilters.groupIds,
    employeeStatus: committedFilters.employeeStatus,
    searchText: debouncedSearchText || undefined,
  });

  const onDownload = useCallback(() => {
    void onExport('EXCEL');
  }, [onExport]);

  const canDownload = hasSearched && totalRecords > 0 && !isCutBlocked;

  // Buscar (único punto de commit explícito): comitea monthlyCutId + el snapshot vigente del
  // panel avanzado (dimensionales + colaboradores seleccionados).
  const handleSearch = useCallback(() => {
    if (!monthlyCutId) return;
    setHasSearched(true);
    setFilters({ monthlyCutId, ...buildDimensionalFilters() });
  }, [monthlyCutId, setFilters, buildDimensionalFilters]);

  const handleSync = useCallback((cut: MonthlyCut) => {
    setYearState(cut.year ?? undefined);
    setMonthState(cut.month ?? undefined);
    setMonthlyCutIdState(cut.monthlyCutId);
  }, []);

  // Auto-búsqueda al cambiar un filtro principal: aplica el corte resuelto junto con los filtros
  // avanzados vigentes, esté el panel expandido o colapsado (ASIS-5589). Colapsar solo oculta el
  // panel; no descarta lo que el usuario configuró.
  const handleAutoSearch = useCallback((resolvedMonthlyCutId: number) => {
    setHasSearched(true);
    setFilters({ monthlyCutId: resolvedMonthlyCutId, ...buildDimensionalFilters() });
  }, [setFilters, buildDimensionalFilters]);

  // Aplica los filtros preaplicados UNA sola vez, ya montado el hook, y con `setFilters` — nunca
  // apoyándose solo en `initialFilters`. Motivo: la grilla arranca con `enabled: false` (hasta que
  // `hasSearched` pasa a true) y `initialFilters` solo se lee en el primer render de
  // `usePaginatedList`, así que por sí solo nunca dispararía la consulta. `setFilters` es el único
  // camino que comitea el corte recibido y deja la grilla cargada sin apretar Buscar.
  const preappliedAppliedRef = useRef(false);
  useEffect(() => {
    if (preappliedFilters === null || preappliedAppliedRef.current) return;
    preappliedAppliedRef.current = true;

    handleAutoSearch(preappliedFilters.monthlyCutId);

    // Se limpian las claves ya consumidas: si quedaran, recargar la pantalla volvería a imponer
    // este corte por sobre el que el usuario haya elegido después. Se borran solo esas claves y no
    // la querystring completa, para no descartar en silencio parámetros ajenos a este mecanismo.
    // `replace` evita agregar una entrada al historial y no rompe el botón Atrás.
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      PREAPPLIED_FILTER_KEYS.forEach((key) => next.delete(key));
      return next;
    }, { replace: true });
  }, [preappliedFilters, handleAutoSearch, setSearchParams]);

  const { markUserSelectedCut, resetAutoSelection } = useMonthlyCutAutoSelection(
    year,
    month,
    monthlyCutId,
    {
      monthlyCutsWithinRange,
      loadingMonthlyCuts,
      scopeKey: selectedCompanyId,
      enabled: autoSelectionEnabled,
      onSync: handleSync,
      onSearch: handleAutoSearch,
    },
  );

  const setSelectedCompanyId = useCallback((newCompanyId: string) => {
    // El colaborador pertenece a una sola empresa: al cambiarla, la selección previa deja de ser
    // válida y employeeIds tiene precedencia sobre los dimensionales en el backend. Los demás
    // filtros avanzados sí se conservan (ASIS-5589).
    // companyJustChangedRef se marca ANTES de clearEmployeeSelection (un setState asíncrono): la
    // auto-búsqueda que este cambio dispara puede ejecutar buildDimensionalFilters con
    // selectedEmployeeIds todavía sin vaciar, así que la ref es la garantía real, no el estado.
    companyJustChangedRef.current = newCompanyId;
    dimensionalFilter.clearEmployeeSelection();
    setHasUserSelectedCompany(true);
    setAutoSelectionEnabled(true);
    resetAutoSelection();
    setSelectedCompanyIdState(newCompanyId);
    setYearState(undefined);
    setMonthState(undefined);
    setMonthlyCutIdState(undefined);
    // Depender solo del método usado (no de `dimensionalFilter` completo, que
    // useEmployeeDimensionalFilter recrea en cada render) evita recrear este callback sin
    // necesidad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAutoSelection, dimensionalFilter.clearEmployeeSelection]);

  const setYear = useCallback((newYear: number | undefined) => {
    setAutoSelectionEnabled(true);
    markUserSelectedCut();
    setYearState(newYear);
    setMonthState(undefined);
    setMonthlyCutIdState(undefined);
  }, [markUserSelectedCut]);

  const setMonth = useCallback((newMonth: number | undefined) => {
    setAutoSelectionEnabled(true);
    markUserSelectedCut();
    setMonthState(newMonth);
    setMonthlyCutIdState(undefined);
  }, [markUserSelectedCut]);

  const setMonthlyCutId = useCallback((newMonthlyCutId: number | undefined) => {
    setAutoSelectionEnabled(true);
    markUserSelectedCut();
    setMonthlyCutIdState(newMonthlyCutId);
  }, [markUserSelectedCut]);

  // Sin polling (decisión aprobada): el desbloqueo se dispara al cambiar de corte, no solo.
  const wasCutRecalculatingRef = useRef(false);
  useEffect(() => {
    if (wasCutRecalculatingRef.current && !isCutRecalculating && monthlyCutId) {
      handleSearch();
    }
    wasCutRecalculatingRef.current = isCutRecalculating;
  }, [isCutRecalculating, monthlyCutId, handleSearch]);

  // Solo el recálculo real colapsa y resetea el panel — el chequeo transitorio del estado del
  // corte (isCheckingCutStatus) se dispara en cada cambio de filtro principal y no debe tratarse
  // como bloqueo para este efecto (ver isCutBlocked, que sí lo incluye para grilla/descarga/
  // Buscar). Es la única excepción a la persistencia de filtros avanzados de ASIS-5589: durante
  // el bloqueo no debe quedar configuración editable pendiente (decisión de negocio).
  useEffect(() => {
    if (isCutRecalculating && dimensionalFilter.isExpanded) {
      dimensionalFilter.resetFilters();
      dimensionalFilter.toggleExpanded();
    }
    // Depender solo de las propiedades usadas (no de `dimensionalFilter` completo, que
    // useEmployeeDimensionalFilter recrea en cada render) evita re-evaluar este efecto sin
    // necesidad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCutRecalculating,
    dimensionalFilter.isExpanded,
    dimensionalFilter.resetFilters,
    dimensionalFilter.toggleExpanded,
  ]);

  // Abre el panel lateral "Detalle error" (ASIS-5318) con el registro completo de la fila.
  const handleViewErrorDetail = useCallback((row: PaymentConceptItem) => {
    openSidePanel({
      title: translate('list.errorDetail.title'),
      component: 'PaymentConceptErrorDetailPanel',
      props: { record: row },
    });
  }, [openSidePanel, translate]);

  const columns: TableColumn<PaymentConceptItem>[] = useMemo(
    () => [
      { key: 'employeeTaxIdentificationNumber', header: translate('list.table.columns.rut'), sortable: true },
      { key: 'employeeName', header: translate('list.table.columns.colaborador'), sortable: true },
      { key: 'company', header: translate('list.table.columns.empresa'), sortable: true },
      { key: 'contract', header: translate('list.table.columns.contrato'), sortable: true },
      { key: 'concept', header: translate('list.table.columns.concepto'), sortable: true },
      { key: 'value', header: translate('list.table.columns.valor'), sortable: true, align: 'right' },
      { key: 'generatedAt', header: translate('list.table.columns.generadoEl'), sortable: true },
      { key: 'previousValue', header: translate('list.table.columns.valorAnterior'), sortable: true, align: 'right' },
      { key: 'sentDate', header: translate('list.table.columns.fechaEnvio'), sortable: true },
      {
        key: 'status',
        header: translate('list.table.columns.estado'),
        sortable: true,
        width: 130,
        overflowStrategy: 'wrap',
        render: (row: PaymentConceptItem) => {
          const statusLabel = React.createElement('span', {
            className: 'block truncate min-w-0',
            title: row.status,
            style: {
              color: STATUS_COLOR_MAP[row.status] ?? colors.grays.neutral66,
              fontWeight: 500,
            },
          }, row.status);

          if (!row.hasError) return statusLabel;

          const menuItems: ActionMenuItem[] = [
            {
              id: 'viewErrorDetail',
              label: translate('common:tableActions.detail'),
              icon: 'eye',
              onClick: () => handleViewErrorDetail(row),
            },
          ];

          return React.createElement(
            'div',
            { className: 'flex items-center justify-between gap-1 min-w-0 w-full' },
            statusLabel,
            React.createElement(
              'div',
              { className: 'shrink-0' },
              React.createElement(ActionMenu, { items: menuItems }),
            ),
          );
        },
      },
    ],
    [translate, handleViewErrorDetail],
  );

  return {
    selectedCompanyId,
    setSelectedCompanyId,
    companyOptions,
    year,
    month,
    monthlyCutId,
    setYear,
    setMonth,
    setMonthlyCutId,
    yearOptions,
    monthOptions: currentMonthOptions,
    monthlyCutOptions,
    loadingMonthlyCuts,
    handleSearch,
    hasSearched,
    // `usePaginatedList` con enabled:false no limpia las filas previas, así que se fuerzan
    // vacías para garantizar el empty-state mientras el corte está bloqueado.
    data: isCutBlocked ? [] : rows,
    totalRecords: isCutBlocked ? 0 : totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
    noCutsAvailable,
    noCutsMessageParts,
    dimensionalConfig: DIMENSIONAL_CONFIG,
    dimensionalFilter,
    isCutRecalculating,
    isCutBlocked,
    recalculationMessageParts,
    onDownload,
    isExporting,
    canDownload,
  };
};
